import { useState } from 'react';
import { ArrowLeft, BookMarked, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { Item } from '@/lib/schema/types';
import { GlossaryDialog } from './GlossaryDialog';

/**
 * Notes for Clarification overlay (wireframe page 4).
 *
 * Renders `notes_for_clarification.general` as a rich paragraph, then each
 * `indicator_notes[]` entry keyed by its `applies_to_codes` so observers can
 * scan directly to the sub-note that applies to the indicator they're scoring.
 *
 * The notes are verbatim from the printed manual, so they carry real
 * structure — multi-paragraph explanations and bulleted material lists (see
 * Dramatic play, Math/number). `Prose` preserves both. Many notes also say
 * "see the Explanation of Terms", so the glossary is one click away from
 * here rather than buried elsewhere.
 */
export function NFCDialog({ item }: { item: Item }) {
  const [open, setOpen] = useState(false);
  const { general, indicator_notes: notes } = item.notes_for_clarification;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <BookOpen className="h-4 w-4" />
          View Notes for Clarification
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden p-0">
        <DialogHeader className="border-b bg-background px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to {item.name}
            </Button>
            <GlossaryDialog
              trigger={
                <Button variant="outline" size="sm" className="gap-2">
                  <BookMarked className="h-4 w-4" />
                  Explanation of Terms
                </Button>
              }
            />
          </div>
          <DialogTitle className="sr-only">
            Notes for Clarification — {item.name}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[calc(85vh-72px)] space-y-4 overflow-y-auto px-6 py-5 text-sm leading-relaxed">
          {general?.trim() ? (
            <Prose text={general} />
          ) : notes.length === 0 ? (
            <p className="rounded-md bg-rating-3/20 p-3 text-xs text-foreground">
              No Notes for Clarification are published for this item.
            </p>
          ) : null}
          {notes.map((n, idx) => (
            <div key={idx} className="border-l-4 border-dece-sky pl-3">
              <div className="mb-1 font-semibold text-dece-navy">
                {n.applies_to_codes.join(', ')}
              </div>
              <Prose text={n.note} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Renders manual prose: blank-line-separated paragraphs, with runs of
 * "- " lines lifted into a real <ul> so material lists stay scannable.
 */
function Prose({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/).filter((b) => b.trim());
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        const bullets = lines.filter((l) => l.trim().startsWith('- '));
        if (bullets.length && bullets.length === lines.filter((l) => l.trim()).length) {
          return (
            <ul key={i} className="ml-1 list-disc space-y-1 pl-4 text-foreground/90">
              {bullets.map((b, j) => (
                <li key={j}>{b.trim().replace(/^-\s*/, '')}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="whitespace-pre-line text-foreground/90">
            {block}
          </p>
        );
      })}
    </>
  );
}
